import { Avatar } from "@package/subframe/components/Avatar";
import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Loader } from "@package/subframe/components/Loader";
import { Progress } from "@package/subframe/components/Progress";
import {
  FeatherAlertCircle,
  FeatherAlertTriangle,
  FeatherCalendarOff,
  FeatherCheck,
  FeatherClipboardList,
  FeatherClock,
  FeatherGauge,
  FeatherRefreshCw,
  FeatherX,
} from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { format, formatDistanceToNow } from "date-fns";

import { CLOSURE_TYPE_LABELS } from "@/components/ClosuresTable/ClosureFormDialog";
import {
  ClosureResponseDto,
  ScheduledQuestionnaireConfigDtoFrequency,
  ScheduledQuestionnaireListItemDto,
} from "@/generated/model";
import { isFailedAnswer } from "@/lib/checklist-utils";
import { formatUkDate, isUkToday } from "@/lib/date-utils";
import { NURSERY_ROLE_LABELS } from "@/lib/roles";

type DisplayStatus =
  | "completed"
  | "completed_late"
  | "in_progress"
  | "missed"
  | "pending";

function getDisplayStatus(
  item: ScheduledQuestionnaireListItemDto,
): DisplayStatus {
  if (item.status === "COMPLETED_LATE") return "completed_late";
  if (item.status === "COMPLETED") return "completed";
  if (item.status === "STARTED") return "in_progress";
  if (item.status === "EXPIRED") return "missed";

  const now = new Date();
  const availableUntil = new Date(item.availableUntil);

  if (now > availableUntil) return "missed";

  return "pending";
}

const FREQUENCY_DISPLAY: Record<
  ScheduledQuestionnaireConfigDtoFrequency,
  string
> = {
  [ScheduledQuestionnaireConfigDtoFrequency.ONE_TIME]: "One-time",
  [ScheduledQuestionnaireConfigDtoFrequency.DAILY]: "Daily",
  [ScheduledQuestionnaireConfigDtoFrequency.WEEKLY]: "Weekly",
  [ScheduledQuestionnaireConfigDtoFrequency.MONTHLY]: "Monthly",
};

function getFrequencyDisplay(
  config: ScheduledQuestionnaireListItemDto["config"],
): string {
  if (config.frequency) {
    return FREQUENCY_DISPLAY[config.frequency] ?? config.frequency;
  }

  if (!config.scheduleRule) return "—";
  const freqMatch = config.scheduleRule.match(/FREQ=(\w+)/);
  if (!freqMatch || !freqMatch[1]) return "—";

  const freq = freqMatch[1].toUpperCase();
  switch (freq) {
    case "DAILY":
      return "Daily";
    case "WEEKLY":
      return "Weekly";
    case "MONTHLY":
      return "Monthly";
    default:
      return freq;
  }
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getIssueCount(item: ScheduledQuestionnaireListItemDto): number {
  return (
    item.answers?.filter((a) => isFailedAnswer(a.answerValue, a.questionType))
      .length || 0
  );
}

function calculateProgress(
  item: ScheduledQuestionnaireListItemDto,
  status: DisplayStatus,
): number {
  if (status === "completed") return 100;
  // questionCount from backend already excludes hidden and non-required questions
  const totalQuestions = item.template.questionCount || 0;
  if (totalQuestions === 0) return 0;
  const relevantAnswers =
    item.answers?.filter((a) => !a.isHidden && a.isRequired !== false) || [];
  const uniqueAnsweredQuestions = new Set(
    relevantAnswers.map((a) => a.questionId),
  );
  const answeredCount = uniqueAnsweredQuestions.size;
  return Math.round((answeredCount / totalQuestions) * 100);
}

function calculateScore(item: ScheduledQuestionnaireListItemDto): number {
  // Score only considers YES_NO / YES_NO_NA answers that are visible and required
  const scoreable =
    item.answers?.filter(
      (a) =>
        !a.isHidden &&
        a.isRequired !== false &&
        (a.questionType === "YES_NO" || a.questionType === "YES_NO_NA"),
    ) || [];
  if (scoreable.length === 0) return 0;
  const positiveAnswers = scoreable.filter((a) => {
    const value = a.answerValue as unknown;
    return typeof value === "string" && value.toLowerCase() === "yes";
  }).length;
  return Math.round((positiveAnswers / scoreable.length) * 100);
}

function getResponsible(item: ScheduledQuestionnaireListItemDto): string {
  if (item.assignedTo) {
    const name = [item.assignedTo.firstName, item.assignedTo.lastName]
      .filter(Boolean)
      .join(" ");
    return name || item.assignedTo.email || "Assigned user";
  }

  const roles = item.config.assigneeRoles;
  if (roles && roles.length > 0) {
    return roles.map((r) => NURSERY_ROLE_LABELS[r] || r).join(", ");
  }

  return "—";
}

interface ChecklistOverviewTabProps {
  dayItems: ScheduledQuestionnaireListItemDto[];
  dayLoading: boolean;
  selectedDate: Date;
  sqActionsInfo: Map<
    string,
    { total: number; open: number; overdue: number; completed: number }
  >;
  closure?: ClosureResponseDto | null;
}

export function ChecklistOverviewTab({
  dayItems,
  dayLoading,
  selectedDate,
  sqActionsInfo,
  closure,
}: ChecklistOverviewTabProps) {
  const navigate = useNavigate();
  const isToday = isUkToday(selectedDate);

  return (
    <div className="w-full overflow-x-auto rounded-sm bg-white">
      {/* Table Header */}
      <div className="flex w-full flex-col items-start">
        <div
          className="flex w-full items-center gap-4 pl-4 pr-4 py-2"
          style={{ minWidth: 900 }}
        >
          <span className="w-48 flex-none text-caption-bold font-caption-bold text-neutral-500">
            Name
          </span>
          <span className="w-28 flex-none text-caption-bold font-caption-bold text-neutral-500">
            Due
          </span>
          <span className="w-20 flex-none text-caption-bold font-caption-bold text-neutral-500">
            Frequency
          </span>
          <span className="w-16 flex-none text-caption-bold font-caption-bold text-neutral-500">
            Score
          </span>
          <span className="w-[144px] flex-none text-caption-bold font-caption-bold text-neutral-500">
            Progress
          </span>
          <span className="w-28 flex-none text-caption-bold font-caption-bold text-neutral-500">
            Responsible
          </span>
          <span className="grow shrink-0 basis-0 text-caption-bold font-caption-bold text-neutral-500 text-right">
            Status
          </span>
          <span className="w-16 flex-none"></span>
        </div>
      </div>

      {/* Closure banner — explains why nothing is due on a non-operating day (NAU-231) */}
      {closure && (
        <div
          className="flex w-full items-center gap-3 rounded-sm bg-secondary-beige-100 px-4 py-3"
          style={{ minWidth: 900 }}
        >
          <Badge variant="warning" icon={<FeatherCalendarOff />}>
            Closed
          </Badge>
          <span className="text-body font-body text-subtext-color">
            {CLOSURE_TYPE_LABELS[closure.type] ?? "Closure"}
            {closure.reason ? ` — ${closure.reason}` : ""}. Scheduled items
            aren&apos;t due, missed or overdue on a closure day.
          </span>
        </div>
      )}

      {/* Today Section Header */}
      <div
        className="flex w-full flex-col items-start pb-1.5"
        style={{ minWidth: 900 }}
      >
        <div className="flex w-full flex-col items-start rounded-sm bg-secondary-beige-100 pl-4 pr-2 py-0.5">
          <span className="text-caption-bold font-caption-bold text-neutral-500 py-2">
            {isToday ? "Today" : format(selectedDate, "EEEE, d MMMM yyyy")}
          </span>
        </div>
      </div>

      {/* Loading State */}
      {dayLoading && (
        <div className="flex w-full items-center justify-center py-8">
          <Loader size="medium" />
        </div>
      )}

      {/* Empty State */}
      {!dayLoading && dayItems.length === 0 && (
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">
            No checklists for{" "}
            {isToday ? "today" : format(selectedDate, "d MMM yyyy")}
          </span>
        </div>
      )}

      {/* Table Rows */}
      {!dayLoading &&
        dayItems.map((item, index) => {
          const status = getDisplayStatus(item);
          const availableUntil = new Date(item.availableUntil);
          const now = new Date();

          const progress = calculateProgress(item, status);
          const score = calculateScore(item);
          const issueCount = getIssueCount(item);

          const relativeTime =
            status === "completed" && item.completedAt
              ? formatDistanceToNow(new Date(item.completedAt), {
                  addSuffix: true,
                })
              : now > availableUntil
                ? formatDistanceToNow(availableUntil, { addSuffix: true })
                : formatDistanceToNow(availableUntil, {
                    addSuffix: true,
                  });

          const isOverdue = now > availableUntil && status !== "completed";
          const daysOverdue = isOverdue
            ? Math.floor(
                (now.getTime() - availableUntil.getTime()) /
                  (1000 * 60 * 60 * 24),
              )
            : 0;

          // The "Due" column is the deadline (availableUntil), not the day
          // the instance was generated for (scheduledDate). For long-windowed
          // cadences (e.g. a Monthly with scheduledDate=Day 1 / availableUntil=
          // Day 28) the two diverge — using scheduledDate would show "1 May"
          // on every day in the window, which is misleading (NAU-233 follow-on).
          const isDueToday = isUkToday(availableUntil);
          const dueTimeText = isDueToday
            ? `Today ${formatUkDate(availableUntil, "H:mm")}`
            : formatUkDate(availableUntil, "d MMM, yyyy");

          return (
            <div className="w-full" key={item.id}>
              <div
                className="flex w-full items-center gap-4 rounded-sm pl-4 pr-4 py-3"
                style={{ minWidth: 900 }}
              >
                {/* Name */}
                <div className="flex w-48 flex-none flex-col items-start">
                  <span
                    className={`text-body-bold font-body-bold text-neutral-900 ${
                      status === "completed" ? "line-through" : ""
                    }`}
                  >
                    {item.template.name}
                  </span>
                  <div className="flex w-full items-center gap-1">
                    <span
                      className={`text-caption font-caption ${
                        isOverdue ? "text-error-700" : "text-subtext-color"
                      }`}
                    >
                      {relativeTime}
                    </span>
                  </div>
                </div>

                {/* Due */}
                <div className="flex w-28 flex-none items-center gap-1">
                  {isOverdue ? (
                    <span className="text-caption font-caption text-error-700">
                      {dueTimeText}
                    </span>
                  ) : (
                    <>
                      <FeatherClock className="text-caption font-caption text-subtext-color" />
                      <span className="text-caption font-caption text-subtext-color">
                        {dueTimeText}
                      </span>
                    </>
                  )}
                </div>

                {/* Frequency */}
                <div className="flex w-20 flex-none items-center gap-1">
                  {item.config.frequency !==
                    ScheduledQuestionnaireConfigDtoFrequency.ONE_TIME && (
                    <FeatherRefreshCw className="text-caption font-caption text-subtext-color" />
                  )}
                  <span className="text-caption font-caption text-subtext-color">
                    {getFrequencyDisplay(item.config)}
                  </span>
                </div>

                {/* Score */}
                <div className="flex w-16 flex-none items-center gap-1">
                  <FeatherGauge className="text-caption font-caption text-subtext-color" />
                  <span className="text-caption font-caption text-subtext-color">
                    {score}%
                  </span>
                </div>

                {/* Progress */}
                <div className="flex w-[144px] flex-none items-center gap-2">
                  <Progress
                    className="min-w-[96px] grow shrink-0 basis-0"
                    value={progress}
                  />
                  <span className="min-w-[36px] text-caption font-caption text-subtext-color">
                    {progress}%
                  </span>
                </div>

                {/* Responsible */}
                <div className="flex w-28 flex-none items-center">
                  {item.assignedTo ? (
                    <span className="text-caption font-caption text-subtext-color truncate">
                      {getResponsible(item)}
                    </span>
                  ) : item.config.assigneeRoles &&
                    item.config.assigneeRoles.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {item.config.assigneeRoles.map((role, idx) => (
                        <Badge key={idx} variant="neutral">
                          {NURSERY_ROLE_LABELS[role] || role}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-caption font-caption text-subtext-color">
                      —
                    </span>
                  )}
                </div>

                {/* Avatars */}
                {item.completedBy && item.completedBy.length > 0 && (
                  <div className="flex items-center">
                    {item.completedBy.slice(0, 3).map((user, idx) => (
                      <Avatar
                        key={user.id}
                        className={idx > 0 ? "-ml-2" : ""}
                        size="small"
                        image=""
                      >
                        {getInitials(
                          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                            "?",
                        )}
                      </Avatar>
                    ))}
                  </div>
                )}

                {/* Status badges */}
                <div className="flex grow shrink-0 basis-0 items-center justify-end gap-2">
                  {/* Overdue badge */}
                  {daysOverdue > 0 && (
                    <Badge variant="error" icon={<FeatherAlertCircle />}>
                      {daysOverdue} day{daysOverdue > 1 ? "s" : ""}{" "}
                      {status === "completed" ? "late" : "overdue"}
                    </Badge>
                  )}

                  {/* Issues badge */}
                  {issueCount > 0 && (
                    <Badge variant="error" icon={<FeatherAlertTriangle />}>
                      {issueCount}
                    </Badge>
                  )}

                  {/* Follow-up actions badge */}
                  {(() => {
                    const info = sqActionsInfo.get(item.id);
                    if (!info || info.total === 0) return null;
                    if (info.overdue > 0) {
                      return (
                        <Badge variant="error" icon={<FeatherClipboardList />}>
                          {info.overdue}
                        </Badge>
                      );
                    }
                    if (info.open > 0) {
                      return (
                        <Badge
                          variant="warning"
                          icon={<FeatherClipboardList />}
                        >
                          {info.open}
                        </Badge>
                      );
                    }
                    if (info.completed > 0) {
                      return (
                        <Badge
                          variant="success"
                          icon={<FeatherClipboardList />}
                        >
                          {info.completed}
                        </Badge>
                      );
                    }
                    return null;
                  })()}

                  {/* Status badge — only rendered for terminal states or
                      when the deadline is today UK. Items that are simply
                      "active in window" go without a badge so we don't
                      mislabel a Monthly mid-window as "Due today" (NAU-233
                      follow-on). */}
                  {status === "completed" ? (
                    <Badge variant="success" icon={<FeatherCheck />}>
                      Completed
                    </Badge>
                  ) : status === "completed_late" ? (
                    <Badge variant="warning" icon={<FeatherCheck />}>
                      Completed late
                    </Badge>
                  ) : status === "in_progress" ? (
                    <Badge>In progress</Badge>
                  ) : status === "missed" || isOverdue ? (
                    <Badge variant="error" icon={<FeatherX />}>
                      Missed
                    </Badge>
                  ) : isDueToday ? (
                    <Badge variant="neutral">Due today</Badge>
                  ) : null}
                </div>

                {/* Actions */}
                <div className="flex w-16 flex-none items-center justify-end">
                  <Button
                    variant="neutral-primary"
                    onClick={() =>
                      navigate({
                        to: "/checklists/$id",
                        params: { id: String(item.id) },
                      })
                    }
                  >
                    View
                  </Button>
                </div>
              </div>

              {/* Divider */}
              {index < dayItems.length - 1 && (
                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
              )}
            </div>
          );
        })}
    </div>
  );
}
